import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import MapView from "@/components/shared/MapView";
import {
  Siren, AlertTriangle, CheckCircle2, Clock, Activity,
  FileText, Download, Navigation, ArrowRightLeft, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import api, { extractApiError } from "@/lib/api";
import {
  fetchIncidentStatusCounts,
  getBySeverityCount,
  getByStatusCount,
  getByStatusSeverityCount,
} from "@/lib/incidentStatusCounts";
import LocationLabel from "@/components/shared/LocationLabel";

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function toText(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    if (("lat" in value || "latitude" in value) && ("lon" in value || "lng" in value || "longitude" in value)) {
      const lat = value.lat ?? value.latitude;
      const lon = value.lon ?? value.lng ?? value.longitude;
      return `${lat}, ${lon}`;
    }
    if (typeof value.address === "string") return value.address;
    if (typeof value.place === "string") return value.place;
    return JSON.stringify(value);
  }
  return String(value);
}

function parseCoords(item) {
  if (!item) return null;
  if (Array.isArray(item.coords) && item.coords.length === 2) return item.coords;
  if (Array.isArray(item.location_coords) && item.location_coords.length === 2) return item.location_coords;
  const location = item.location;
  if (location && typeof location === "object") {
    const lat = Number.parseFloat(location.lat ?? location.latitude);
    const lng = Number.parseFloat(location.lon ?? location.lng ?? location.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  const lat = Number.parseFloat(item.latitude ?? item.lat ?? item.lat_deg ?? item.latitude_deg);
  const lng = Number.parseFloat(item.longitude ?? item.lng ?? item.lon ?? item.longitude_deg);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
}

function normalizeOpenIncident(item) {
  const primaryVehicle = Array.isArray(item?.vehicles)
    ? item.vehicles.find((v) => v?.role === "PRIMARY") || item.vehicles[0]
    : null;

  return {
    id: item?.id || item?.incident_id || "",
    type: item?.incident_type || item?.type || "Incident",
    severity: String(item?.severity || "low").toLowerCase(),
    vehicle: primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || item?.vehicle || item?.plate || "—",
    location: toText(item?.location || item?.address || item?.place || "—"),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    coords: parseCoords(item),
    reportedAt: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    raw: item,
  };
}

function normalizeVehicleState(item) {
  const status = String(item?.computed_status || item?.status || item?.state || "offline").toUpperCase();
  const coords = parseCoords(item);
  return {
    id: item?.vehicle_id || item?.id || "",
    registration: toText(item?.registration_number || item?.plate || item?.vehicle_name || "—"),
    type: toText(item?.vehicle_type || item?.type || "Vehicle"),
    status,
    speed: Number.parseFloat(item?.speed ?? item?.gps_speed ?? 0) || 0,
    location: toText(item?.location || item?.address || "—"),
    locationRaw: item?.location ?? item?.address ?? null,
    coords,
    raw: item,
  };
}

function normalizeAssignedUnits(item) {
  if (!Array.isArray(item?.assigned_units)) return [];

  return item.assigned_units
    .map((unit) => {
      if (typeof unit === "string" || typeof unit === "number") {
        const text = String(unit);
        return { id: text, label: text };
      }

      if (!unit || typeof unit !== "object") return null;

      const id = unit.id || unit.vehicle_id || unit.unit_id || "";
      const label =
        unit.registration_number
        || unit.registration
        || unit.plate
        || unit.vehicle_label
        || unit.vehicle_name
        || unit.call_sign
        || (id ? String(id) : "");

      if (!id && !label) return null;
      return {
        id: id ? String(id) : String(label),
        label: String(label || id),
      };
    })
    .filter((unit) => unit?.id && unit?.label);
}

function normalizeDispatchIncident(item) {
  const assignedUnits = normalizeAssignedUnits(item);
  const primaryVehicle = Array.isArray(item?.vehicles)
    ? item.vehicles.find((v) => v?.role === "PRIMARY") || item.vehicles[0]
    : null;

  return {
    id: item?.id || item?.incident_id || "",
    type: item?.incident_type || item?.type || "Incident",
    location: toText(item?.location || item?.address || item?.place || "—"),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    severity: String(item?.severity || "low").toLowerCase(),
    status: String(item?.status || item?.current_status || "OPEN").toUpperCase(),
    vehicle: assignedUnits.length > 0
      ? assignedUnits.map((unit) => unit.label).join(", ")
      : (primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || item?.vehicle || item?.plate || "—"),
    assignedUnits,
    driver: item?.driver?.name || item?.driver?.user_id || item?.driver || "—",
    coords: parseCoords(item),
    updatedAt: item?.updated_at || item?.modified_at || item?.created_at || item?.timestamp || item?.event_time || "",
    raw: item,
  };
}

function normalizeCompletedIncident(item) {
  const primaryVehicle = Array.isArray(item?.vehicles)
    ? item.vehicles.find((v) => v?.role === "PRIMARY") || item.vehicles[0]
    : null;

  const type = item?.incident_type || item?.type || "Incident";
  const location = toText(item?.location || item?.address || item?.place || "—");
  const locationRaw = item?.location ?? item?.address ?? item?.place ?? null;
  const registrationNumber = primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || item?.vehicle || item?.plate || "—";
  const driverName = item?.driver?.name || "—";

  return {
    id: item?.id || item?.incident_id || "",
    type,
    location,
    locationRaw,
    vehicle: registrationNumber,
    driverName,
    severity: String(item?.severity || "low").toLowerCase(),
    caseIdentifier: `${registrationNumber} • ${type}`,
    summary: `${type} at ${location} (Driver: ${driverName})`,
    completedAt: item?.resolved_at || item?.closed_at || item?.updated_at || item?.timestamp || item?.created_at || "",
    raw: item,
  };
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString();
}

export default function PoliceDashboard() {
  const [mapView, setMapView] = useState('street');
  const [selectedCase, setSelectedCase] = useState(null);
  const [featuredCase, setFeaturedCase] = useState(null);
  const [overview, setOverview] = useState({
    activeCases: null,
    newAlerts: null,
    completedCases: null,
    highPriorityAlerts: null,
  });
  const [fleetAvailability, setFleetAvailability] = useState({
    total: null,
    busy: null,
    available: null,
    activeIncidents: null,
  });
  const [openIncidents, setOpenIncidents] = useState([]);
  const [vehicleStates, setVehicleStates] = useState([]);
  const [dashboardActiveCases, setDashboardActiveCases] = useState([]);
  const [dashboardCompletedCases, setDashboardCompletedCases] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [ackSubmittingId, setAckSubmittingId] = useState("");
  const [dispatchSubmittingId, setDispatchSubmittingId] = useState("");
  const [completeSubmittingId, setCompleteSubmittingId] = useState("");
  const [completedPage, setCompletedPage] = useState(1);
  const [completedPageSize, setCompletedPageSize] = useState(5);
  const [completedMeta, setCompletedMeta] = useState({ total: 0, page: 1, page_size: 5, pages: 1 });

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      try {
        const [statusCounts, fleetAvailabilityResponse] = await Promise.all([
          fetchIncidentStatusCounts(),
          api.get("/analytics/fleet/availability"),
        ]);

        if (cancelled) return;

        const byStatus = statusCounts?.by_status || {};
        const byStatusSeverity = statusCounts?.by_status_severity || {};

        const openCount = getByStatusCount(byStatus, "OPEN");
        const acknowledgedCount = getByStatusCount(byStatus, "ACKNOWLEDGED");
        const respondingCount = getByStatusCount(byStatus, "RESPONDING");
        const resolvedCount = getByStatusCount(byStatus, "RESOLVED");

        // Prefer OPEN-by-severity matrix when available; fall back to flat severity totals.
        const openCritical = getByStatusSeverityCount(byStatusSeverity, "OPEN", "CRITICAL");
        const openHigh = getByStatusSeverityCount(byStatusSeverity, "OPEN", "HIGH");
        const highPriorityAlerts = (openCritical || openHigh)
          ? openCritical + openHigh
          : getBySeverityCount(byStatusSeverity, "CRITICAL") + getBySeverityCount(byStatusSeverity, "HIGH");

        setOverview({
          newAlerts: openCount,
          activeCases: acknowledgedCount + respondingCount,
          completedCases: resolvedCount,
          highPriorityAlerts,
        });

        const fleet = fleetAvailabilityResponse?.data?.data || {};
        setFleetAvailability({
          total: Number.isFinite(Number(fleet.total)) ? Number(fleet.total) : 0,
          busy: Number.isFinite(Number(fleet.busy)) ? Number(fleet.busy) : 0,
          available: Number.isFinite(Number(fleet.available)) ? Number(fleet.available) : 0,
          activeIncidents: Number.isFinite(Number(fleet.active_incidents)) ? Number(fleet.active_incidents) : 0,
        });
      } catch (err) {
        if (cancelled) return;
        const { message } = extractApiError(err);
        toast.error(message || "Failed to load police dashboard overview.");
      }
    };

    loadOverview();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadDashboardSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const [openResponse, acknowledgedResponse, respondingResponse, resolvedResponse] = await Promise.all([
        api.get("/incidents", { params: { status: "OPEN", page: 1, page_size: 4 } }),
        api.get("/incidents", { params: { status: "ACKNOWLEDGED", page_size: 50 } }),
        api.get("/incidents", { params: { status: "RESPONDING", page_size: 50 } }),
        api.get("/incidents", { params: { status: "RESOLVED", page: completedPage, page_size: completedPageSize } }),
      ]);

      const nextOpen = normalizeCollection(openResponse?.data)
        .map(normalizeOpenIncident)
        .filter((item) => item.id);
      const nextAcknowledged = normalizeCollection(acknowledgedResponse?.data).map(normalizeDispatchIncident);
      const nextResponding = normalizeCollection(respondingResponse?.data).map(normalizeDispatchIncident);
      const nextResolved = normalizeCollection(resolvedResponse?.data)
        .map(normalizeCompletedIncident)
        .filter((item) => item.id);

      const nextActive = [...nextAcknowledged, ...nextResponding]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      setOpenIncidents(nextOpen);
      setDashboardActiveCases(nextActive);
      setDashboardCompletedCases(nextResolved);
      const resolvedMeta = resolvedResponse?.data?.meta || {};
      setCompletedMeta({
        total: Number(resolvedMeta.total) || 0,
        page: Number(resolvedMeta.page) || Number(completedPage) || 1,
        page_size: Number(resolvedMeta.page_size) || Number(completedPageSize) || 5,
        pages: Number(resolvedMeta.pages) || 1,
      });
      setFeaturedCase((current) => {
        if (!current?.id) return nextActive[0] || null;
        return nextActive.find((item) => item.id === current.id) || nextActive[0] || null;
      });
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load police dashboard sections.");
    } finally {
      setSectionsLoading(false);
    }
  }, [completedPage, completedPageSize]);

  useEffect(() => {
    loadDashboardSections();
  }, [loadDashboardSections]);

  useEffect(() => {
    let cancelled = false;

    const loadMapData = async () => {
      try {
        const vehicleStateResponse = await api.get("/vehicles/bulk-state", { params: { only_live: true, page_size: 100 } });

        if (cancelled) return;

        const nextVehicleStates = normalizeCollection(vehicleStateResponse?.data)
          .map(normalizeVehicleState)
          .filter((vehicle) => vehicle.id);

        setVehicleStates(nextVehicleStates);
      } catch (err) {
        if (cancelled) return;
        const { message } = extractApiError(err);
        toast.error(message || "Failed to load police map markers.");
      }
    };

    loadMapData();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleExportCSV = () => {
    const data = [
      ['Active Cases', overview.activeCases ?? 0],
      ['New Alerts', overview.newAlerts ?? 0],
      ['Completed Cases', overview.completedCases ?? 0],
      ['High Priority Alerts', overview.highPriorityAlerts ?? 0],
    ];
    downloadReport({
      filename: "police_dashboard_summary",
      title: "Police Dashboard Summary",
      headers: ["Metric", "Value"],
      rows: data,
    });
    toast.success("CSV exported");
  };

  const handleExportPDF = () => {
    // Mock PDF export
    toast.success("PDF exported");
  };

  const handleAcceptAlert = async (alertId) => {
    if (!alertId) return;
    setAckSubmittingId(alertId);
    try {
      await api.post(`/incidents/${alertId}/service-response`, { status: "ACKNOWLEDGED" });
      toast.success(`Incident ${alertId} acknowledged.`);
      await loadDashboardSections();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to acknowledge incident.");
    } finally {
      setAckSubmittingId("");
    }
  };

  const handleLogDispatch = async (incidentId) => {
    if (!incidentId) return;
    setDispatchSubmittingId(incidentId);
    try {
      await api.post(`/incidents/${incidentId}/service-response`, {
        status: "DISPATCHED",
        note: "Dispatch logged from Police Dashboard",
      });
      toast.success(`Dispatch note logged for incident ${incidentId}.`);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to log dispatch response.");
    } finally {
      setDispatchSubmittingId("");
    }
  };

  const handleStartNavigation = (caseId) => {
    setSelectedCase(caseId);
    toast.success("Navigation started");
  };

  const handleTransferCase = (caseId) => {
    toast.info(`Transfer workflow for ${caseId} is not yet backed by the API.`);
  };

  const handleCompleteCase = async (caseId) => {
    if (!caseId) return;
    setCompleteSubmittingId(caseId);
    try {
      await api.post(`/incidents/${caseId}/service-response`, {
        status: "RESOLVED",
        note: "Terminal response logged from Police Dashboard",
      });
      toast.success(`Terminal response logged for incident ${caseId}.`);
      await loadDashboardSections();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to complete incident.");
    } finally {
      setCompleteSubmittingId("");
    }
  };

  const handleSetFeatured = (caseId) => {
    setFeaturedCase(dashboardActiveCases.find((item) => item.id === caseId) || null);
  };

  // Prepare map markers
  const mapMarkers = useMemo(() => ([
    ...openIncidents
      .filter((incident) => Array.isArray(incident.coords) && incident.coords.length === 2)
      .map((incident) => ({
        coords: incident.coords,
        title: incident.type || "Incident",
        subtitle: incident.location,
        severity: incident.severity,
        type: 'alert',
      })),
    ...dashboardActiveCases
      .filter((caseItem) => Array.isArray(caseItem.coords) && caseItem.coords.length === 2)
      .map((caseItem) => ({
        coords: caseItem.coords,
        title: caseItem.type || "Incident",
        subtitle: caseItem.location,
        severity: caseItem.severity,
        type: 'case',
      })),
    ...vehicleStates
      .filter((vehicle) => Array.isArray(vehicle.coords) && vehicle.coords.length === 2)
      .map((vehicle) => ({
        coords: vehicle.coords,
        title: vehicle.registration || vehicle.type,
        subtitle: vehicle.location,
        severity: vehicle.status === 'OFFLINE' ? 'low' : vehicle.speed > 80 ? 'high' : 'medium',
        type: 'unit',
        status: vehicle.status,
      })),
  ]), [dashboardActiveCases, openIncidents, vehicleStates]);

  return (
    <div className="space-y-6" data-testid="police-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-700 to-fuchsia-600 bg-clip-text text-transparent drop-shadow-sm">Police Emergency Command</h1>
          <p className="text-sm text-slate-500 font-medium mt-1">Real-time command view for alerts, active response, unit assignments, and incident mapping.</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Activity size={12} className="mr-1" />
            Polling Active
          </Badge>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download size={14} className="mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText size={14} className="mr-1" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <KPITile icon={Siren} label="Active Cases" value={overview.activeCases == null ? "—" : String(overview.activeCases)} accent="role" />
        <KPITile icon={AlertTriangle} label="New Alerts" value={overview.newAlerts == null ? "—" : String(overview.newAlerts)} accent="rose" />
        <KPITile icon={CheckCircle2} label="Completed Cases" value={overview.completedCases == null ? "—" : String(overview.completedCases)} accent="emerald" />
        <KPITile icon={Clock} label="High Priority Alerts" value={overview.highPriorityAlerts == null ? "—" : String(overview.highPriorityAlerts)} accent="amber" />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Left Column */}
        <div className="space-y-6 xl:col-span-2">
          {/* Live Incident Map */}
          <SectionCard title="Live Incident Map" subtitle="Incidents, assigned units, and response route rendered inside the dashboard.">
            <div className="space-y-3">
              {/* <div className="flex gap-2">
                <Button
                  variant={mapView === 'street' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMapView('street')}
                >
                  Street View
                </Button>
                <Button
                  variant={mapView === 'satellite' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMapView('satellite')}
                >
                  Satellite View
                </Button>
              </div> */}
              <MapView
                markers={mapMarkers}
                zoom={12}
                height={400}
                testId="police-map"
              />
            </div>
          </SectionCard>

          {/* Command Snapshot */}
          <SectionCard title="Command Snapshot" subtitle="Current operational posture across the police network.">
            <div className="grid grid-cols-2 gap-4 mb-6 lg:grid-cols-5">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {fleetAvailability.available == null ? "—" : fleetAvailability.available}
                </div>
                <div className="text-xs text-slate-500">Available Units</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {fleetAvailability.busy == null ? "—" : fleetAvailability.busy}
                </div>
                <div className="text-xs text-slate-500">Busy Units</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {fleetAvailability.activeIncidents == null ? "—" : fleetAvailability.activeIncidents}
                </div>
                <div className="text-xs text-slate-500">Active Incidents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-600">
                  {fleetAvailability.total == null ? "—" : fleetAvailability.total}
                </div>
                <div className="text-xs text-slate-500">Total Units</div>
              </div>
            </div>

            {featuredCase && (
              <div className="border-t pt-4">
                <h4 className="font-semibold text-slate-900 mb-3">Featured Incident</h4>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-slate-600">{featuredCase.type}</p>
                      <LocationLabel locationValue={featuredCase.locationRaw ?? featuredCase.location} className="text-xs text-slate-500" />
                    </div>
                    <SeverityBadge severity={featuredCase.severity} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleStartNavigation(featuredCase.id)}>
                      <Navigation size={14} className="mr-1" />
                      Start Navigation
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleTransferCase(featuredCase.id)}>
                      <ArrowRightLeft size={14} className="mr-1" />
                      Transfer Case
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleCompleteCase(featuredCase.id)}>
                      <Check size={14} className="mr-1" />
                      Mark Completed
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* New Alerts Queue */}
          <SectionCard title="New Alerts Queue" subtitle="Incoming emergency alerts with direct assignment and dispatch actions.">
            <div className="space-y-3">
              {openIncidents.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{alert.type}</p>
                    <LocationLabel locationValue={alert.locationRaw ?? alert.location} className="text-xs text-slate-500" />
                    <p className="text-xs text-slate-400">{formatTime(alert.reportedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleLogDispatch(alert.id)}
                      disabled={dispatchSubmittingId === alert.id}
                    >
                      {dispatchSubmittingId === alert.id ? "Logging..." : "Dispatch"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAcceptAlert(alert.id)}
                      disabled={ackSubmittingId === alert.id}
                    >
                      {ackSubmittingId === alert.id ? "Saving..." : <Check size={14} />}
                    </Button>
                  </div>
                </div>
              ))}
              {!sectionsLoading && openIncidents.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No new alerts</p>
              )}
            </div>
          </SectionCard>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Fleet Availability */}
          <SectionCard title="Fleet Availability" subtitle="Live summary from fleet analytics for police dispatch readiness.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 xl:grid-cols-1">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Available</p>
                <p className="mt-1 text-2xl font-bold text-green-600">
                  {fleetAvailability.available == null ? "—" : fleetAvailability.available}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Busy</p>
                <p className="mt-1 text-2xl font-bold text-blue-600">
                  {fleetAvailability.busy == null ? "—" : fleetAvailability.busy}
                </p>
              </div>
               <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Active Incidents</p>
                <p className="mt-1 text-2xl font-bold text-orange-600">
                  {fleetAvailability.activeIncidents == null ? "—" : fleetAvailability.activeIncidents}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total</p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {fleetAvailability.total == null ? "—" : fleetAvailability.total}
                </p>
              </div>
            </div>
          </SectionCard>

          {/* Active Dispatch Board */}
          <SectionCard title="Active Dispatch Board" subtitle="Live cases with assignment control and response actions.">
            <div className="space-y-3">
              {dashboardActiveCases.map((caseItem) => (
                <div key={caseItem.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <SeverityBadge severity={caseItem.severity} />
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{caseItem.type}</p>
                  <LocationLabel locationValue={caseItem.locationRaw ?? caseItem.location} className="text-xs text-slate-500" />
                  <StatusBadge status={caseItem.status} className="mb-2" />
                  <p className="text-xs text-slate-600 mb-2">Assigned Units: {caseItem.vehicle}</p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleSetFeatured(caseItem.id)}>
                      Feature
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleStartNavigation(caseItem.id)}>
                      Navigate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => handleCompleteCase(caseItem.id)}
                      disabled={completeSubmittingId === caseItem.id}
                    >
                      {completeSubmittingId === caseItem.id ? "Saving..." : "Complete"}
                    </Button>
                  </div>
                </div>
              ))}
              {!sectionsLoading && dashboardActiveCases.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No active cases</p>
              )}
            </div>
          </SectionCard>

          {/* Recent Case History */}
          <SectionCard title="Recent Case History" subtitle="Completed cases and reporting output from the live dashboard.">
            <div className="space-y-3">
              {dashboardCompletedCases.map((caseItem) => (
                <div key={caseItem.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex-1">
                    <p className="font-mono text-xs font-semibold text-slate-700">{caseItem.caseIdentifier}</p>
                    <p className="text-xs text-slate-600">{caseItem.summary}</p>
                    <p className="text-xs text-slate-400">{formatTime(caseItem.completedAt)}</p>
                  </div>
                  <SeverityBadge severity={caseItem.severity} />
                </div>
              ))}
              {!sectionsLoading && dashboardCompletedCases.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No completed cases</p>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 mt-4">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCompletedPage((p) => Math.max(1, p - 1))}
                  disabled={completedPage <= 1}
                >
                  Prev
                </Button>
                <div className="text-sm text-slate-600 px-2">Page {completedPage} / {completedMeta.pages || 1}</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCompletedPage((p) => Math.min((completedMeta.pages || 1), p + 1))}
                  disabled={completedPage >= (completedMeta.pages || 1)}
                >
                  Next
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-600">Per page</div>
                <Select
                  value={String(completedPageSize)}
                  onValueChange={(v) => {
                    const newSize = Number(v) || 5;
                    setCompletedPageSize(newSize);
                    setCompletedPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-shrink-0 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={handleExportCSV}>
                  <Download size={12} className="mr-1" />
                  CSV
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={handleExportPDF}>
                  <FileText size={12} className="mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
